package com.trevorwiebe.timesheet.admin.reporting.presentation

import com.trevorwiebe.timesheet.admin.reporting.domain.IftaStateTotal
import com.trevorwiebe.timesheet.admin.reporting.domain.LoadWithoutTruck
import com.trevorwiebe.timesheet.admin.reporting.domain.RevenuePerMileReport
import com.trevorwiebe.timesheet.admin.reporting.domain.TruckMileageTotal
import kotlin.math.abs
import kotlin.math.roundToLong

data class CsvFile(val fileName: String, val content: String)

fun ReportingState.toCsvFile(): CsvFile? {
    val start = filter.startDate
    val end = filter.endDate
    val range = if (start != null && end != null) "_${start}_to_$end" else ""
    return when (reportType) {
        ReportType.IFTA -> CsvFile(
            fileName = "IFTA$range.csv",
            content = iftaCsv(iftaRows),
        )

        ReportType.GENERAL_MILEAGE -> CsvFile(
            fileName = "General_Mileage$range.csv",
            content = generalMileageCsv(generalMileageRows),
        )
        ReportType.MISSING_START_END -> null

        ReportType.REVENUE_PER_MILE -> CsvFile(
            fileName = "Revenue_Per_Mile$range.csv",
            content = revenuePerMileCsv(revenuePerMile),
        )

        ReportType.LOADS_WITHOUT_TRUCK -> CsvFile(
            fileName = "Loads_Without_Truck$range.csv",
            content = loadsWithoutTruckCsv(loadsWithoutTruckRows),
        )
    }
}

private fun revenuePerMileCsv(report: RevenuePerMileReport): String {
    val sb = StringBuilder()
    sb.appendLine("Truck,Loads,Revenue,Miles,Revenue Per Mile,Revenue Per Mile (No Fuel Surcharge)")
    report.rows.forEach { row ->
        sb.appendLine(
            "${row.unitNumber.csvField()},${row.loadCount},${row.revenue.centsCsv()},${row.miles}," +
                "${perMileCsv(row.revenue, row.miles)},${perMileCsv(row.revenueExFuel, row.miles)}"
        )
    }
    if (report.unassignedLoadCount > 0) {
        sb.appendLine("No truck,${report.unassignedLoadCount},${report.unassignedRevenue.centsCsv()},,,")
    }
    return sb.toString()
}

private fun loadsWithoutTruckCsv(rows: List<LoadWithoutTruck>): String {
    val sb = StringBuilder()
    sb.appendLine("Date,Load #,Customer,Driver,Drops,Possible Trucks")
    rows.forEach { row ->
        sb.appendLine(
            listOf(
                row.date.toString(),
                row.loadNumber,
                row.customerName,
                row.driverName,
                row.dropNames,
                row.candidateUnits.joinToString(" / "),
            ).joinToString(",") { it.csvField() }
        )
    }
    return sb.toString()
}

private fun perMileCsv(cents: Int, miles: Long): String =
    if (miles <= 0L) "" else (cents.toDouble() / miles / 100.0).twoDecimals()

/** Cents as plain dollars with no thousands separators: 123456 -> "1234.56". */
private fun Int.centsCsv(): String {
    val sign = if (this < 0) "-" else ""
    return "$sign${abs(this) / 100}.${(abs(this) % 100).toString().padStart(2, '0')}"
}

private fun iftaCsv(rows: List<IftaStateTotal>): String {
    val sb = StringBuilder()
    sb.appendLine("State,Mileage,Gallons")
    rows.forEach { row ->
        sb.appendLine("${row.stateName.csvField()},${row.miles},${row.gallons.oneDecimal()}")
    }
    return sb.toString()
}

private fun generalMileageCsv(rows: List<TruckMileageTotal>): String {
    val sb = StringBuilder()
    sb.appendLine("Truck,Miles,Gallons,MPG")
    rows.forEach { row ->
        sb.appendLine(
            "${row.unitNumber.csvField()},${row.totalMiles}," +
                "${row.totalGallons.oneDecimal()},${row.milesPerGallon.twoDecimals()}"
        )
    }
    return sb.toString()
}

/** Formats a value with a single decimal place and no thousands separators. */
private fun Double.oneDecimal(): String {
    val totalTenths = (this * 10).roundToLong()
    return "${totalTenths / 10}.${totalTenths % 10}"
}

/** Formats a value with two decimal places and no thousands separators. */
private fun Double.twoDecimals(): String {
    val totalHundredths = (this * 100).roundToLong()
    return "${totalHundredths / 100}.${(totalHundredths % 100).toString().padStart(2, '0')}"
}

/** Quotes and escapes a field if it contains a comma, quote, or newline. */
private fun String.csvField(): String =
    if (any { it == ',' || it == '"' || it == '\n' || it == '\r' }) {
        "\"${replace("\"", "\"\"")}\""
    } else {
        this
    }
